import { useFetchLegends } from '@app/hooks/useFetchLegends'
import {
  applyVulnerabilityDataAtom,
  layerLoadingAtom,
} from '@app/risk-migration/atoms/riskLayers.atoms'
import { useFetchLayerStyles } from '@app/services/legendService'
import { useAtomValue, useSetAtom } from 'jotai'
import { useEffect, useMemo } from 'react'
import { riskMapInstanceAtom } from '../atoms/riskMap.atoms'
import {
  drawAreasAtom,
  hasRiskFiltersAtom,
  nonEmptyElementFiltersAtom,
  regionMasksAtom,
} from '../atoms/riskFilters.atoms'
import { renderRiskLayers } from '../renderRiskLayers'
import { useCurrentVulnerabilityData } from './useCurrentVulnerabilityData'
import { useRiskLayersState } from './useRiskLayersState'
import { useVisibleAssets } from './useVisibleAssets'

const EMPTY_MAP_LAYER_STYLES = {}
const EMPTY_LEGENDS_DATA = {}

export function useSyncLayersToMap() {
  const map = useAtomValue(riskMapInstanceAtom)
  const { data: mapLayerStyles = EMPTY_MAP_LAYER_STYLES } = useFetchLayerStyles()
  const { data: legendsData = EMPTY_LEGENDS_DATA } = useFetchLegends()
  const { layers } = useRiskLayersState()
  const drawAreas = useAtomValue(drawAreasAtom)
  const regionMasks = useAtomValue(regionMasksAtom)
  const elementFilters = useAtomValue(nonEmptyElementFiltersAtom)
  const hasFilters = useAtomValue(hasRiskFiltersAtom)
  const applyVulnerabilityData = useAtomValue(applyVulnerabilityDataAtom)

  const setLayerLoading = useSetAtom(layerLoadingAtom)

  const { visibleAssets, calculateVisibleAssets } = useVisibleAssets()

  const assetLayers = useMemo(() => layers.filter((layer) => layer.layerType === 'asset'), [layers])

  const contextualLayers = useMemo(
    () => layers.filter((layer) => layer.layerType === 'information'),
    [layers],
  )

  const { vulnerabilityData, vulnerabilityLoading } = useCurrentVulnerabilityData()

  useEffect(() => {
    if (!map) return

    let cancelled = false

    const sync = async () => {
      if (applyVulnerabilityData && vulnerabilityLoading) return
      setLayerLoading(true)

      try {
        const nextVisibleAssets = await calculateVisibleAssets({
          map,
          assetLayers,
          contextualLayers,
          drawAreas,
          regionMasks,
          hasFilters,
          elementFilters,
        })

        if (cancelled) return

        renderRiskLayers({
          map,
          layers,
          mapLayerStyles,
          visibleAssets: nextVisibleAssets,
          vulnerabilityData,
          legendsData,
          regionMasks,
          hasFilters,
          applyVulnerabilityData,
        })
      } catch (error) {
        console.error('Failed to sync risk layers to map', error)
      } finally {
        if (!cancelled) {
          setLayerLoading(false)
        }
      }
    }

    if (map.isStyleLoaded()) {
      void sync()

      return () => {
        cancelled = true
      }
    }

    // The style might not be ready yet. We can't rely on one style.load event.
    // Re-check readiness on the events that keep firing as the style settles and sync once.
    let settled = false
    const trySync = () => {
      if (settled || cancelled || !map.isStyleLoaded()) return
      settled = true
      map.off('styledata', trySync)
      map.off('idle', trySync)
      void sync()
    }

    map.on('styledata', trySync)
    map.on('idle', trySync)

    return () => {
      cancelled = true
      map.off('styledata', trySync)
      map.off('idle', trySync)
    }
  }, [
    map,
    layers,
    assetLayers,
    contextualLayers,
    drawAreas,
    regionMasks,
    elementFilters,
    hasFilters,
    applyVulnerabilityData,
    vulnerabilityData,
    vulnerabilityLoading,
    mapLayerStyles,
    legendsData,
    calculateVisibleAssets,
    setLayerLoading,
  ])

  return {
    visibleAssets,
    vulnerabilityData,
    vulnerabilityLoading,
  }
}

export function RiskMapSync() {
  useSyncLayersToMap()
  return null
}
